import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Image, Input, message, Modal, Typography, Upload } from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { shipmentApi, type ManualConfirmDeliveryResultDTO, type ShipmentDTO } from '../api/kfc';

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_TEXT = 500;
const ALLOWED_TYPES = ['image/png', 'image/jpeg'];

type PendingImage = { uid: string; file: File; url: string };

interface Props {
  shipment: ShipmentDTO | null;
  onClose: () => void;
  onConfirmed: (result: ManualConfirmDeliveryResultDTO) => void;
}

const ManualConfirmDeliveryModal: React.FC<Props> = ({ shipment, onClose, onConfirmed }) => {
  const [form] = Form.useForm<{ note?: string }>();
  const [images, setImages] = useState<PendingImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const imagesRef = useRef<PendingImage[]>([]);
  imagesRef.current = images;
  const open = !!shipment;

  const clearImages = useCallback(() => {
    imagesRef.current.forEach((img) => URL.revokeObjectURL(img.url));
    setImages([]);
  }, []);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      clearImages();
    }
  }, [open, form, clearImages]);

  const addFiles = useCallback((files: File[]) => {
    const current = imagesRef.current;
    const accepted: PendingImage[] = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type) && !/\.(png|jpe?g)$/i.test(f.name || '')) {
        message.warning(`图片格式仅支持 png / jpg：${f.name || '图片'}`);
        continue;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        message.warning(`单张图片不能超过 10MB：${f.name || '图片'}`);
        continue;
      }
      if (current.length + accepted.length >= MAX_IMAGES) {
        message.warning(`图片最多 ${MAX_IMAGES} 张`);
        break;
      }
      accepted.push({ uid: `${Date.now()}-${Math.random()}`, file: f, url: URL.createObjectURL(f) });
    }
    if (accepted.length) setImages([...current, ...accepted]);
  }, []);

  const removeImage = (uid: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.uid === uid);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.uid !== uid);
    });
  };

  const onOk = () => {
    if (!shipment) return;
    form.validateFields().then((values) => {
      const note = values.note?.trim() || '';
      if (!note && images.length === 0) {
        message.warning('请填写确认说明或上传图片依据');
        return;
      }
      const fd = new FormData();
      if (note) fd.append('note', note);
      images.forEach((img) => fd.append('files', img.file, img.file.name));
      setSubmitting(true);
      shipmentApi
        .manualConfirmDelivery(shipment.shipmentId, fd)
        .then((res) => {
          message.success(res.data.problemResolved ? '已确认签收，问题件已解除' : '已确认签收');
          onConfirmed(res.data);
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'))
        .finally(() => setSubmitting(false));
    });
  };

  return (
    <Modal
      title={`确认签收 ${shipment?.shipmentId ?? ''}`}
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText="确认签收"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        代理轨迹未更新到签收时，可人工确认。提交后运单将锁定为已签收，并写入轨迹依据。
      </Typography.Paragraph>
      <Form form={form} layout="vertical">
        <Form.Item name="note" label="确认说明" extra={`文字或图片至少一项，最多 ${MAX_TEXT} 字`}>
          <Input.TextArea rows={3} maxLength={MAX_TEXT} placeholder="例如：客户微信确认已签收" />
        </Form.Item>
      </Form>
      <Upload.Dragger
        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
        multiple
        showUploadList={false}
        beforeUpload={(file) => {
          addFiles([file]);
          return false;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">上传签收依据图片（png / jpg，最多 5 张）</p>
      </Upload.Dragger>
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {images.map((img) => (
            <div key={img.uid} style={{ position: 'relative' }}>
              <Image src={img.url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} />
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeImage(img.uid)}
                style={{ position: 'absolute', top: -6, right: -6 }}
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default ManualConfirmDeliveryModal;
